import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { OrgMember, OrgWithMembers, OrgSettingsInput } from '../types';

interface UseAdminOrgsReturn {
  orgs: OrgWithMembers[];
  loading: boolean;
  createOrg: (name: string) => Promise<boolean>;
  removeMember: (userId: string, orgId: string) => Promise<'ok' | 'error'>;
  updateOrgSettings: (orgId: string, values: Partial<OrgSettingsInput>) => Promise<boolean>;
}

export function useAdminOrgs(): UseAdminOrgsReturn {
  const [orgs, setOrgs] = useState<OrgWithMembers[]>([]);
  const [loading, setLoading] = useState(true);

  const loadOrgs = async () => {
    setLoading(true);
    try {
      const [{ data: orgData }, { data: memberData }] = await Promise.all([
        supabase.from('organizations').select('id, name, max_active_projects, pickup_handin_enabled').order('name'),
        supabase.from('organization_members').select('user_id, organization_id, email, active, role'),
      ]);
      setOrgs(
        (orgData ?? []).map((org: { id: string; name: string; max_active_projects: number | null; pickup_handin_enabled: boolean }) => ({
          ...org,
          members: (memberData ?? []).filter((m: OrgMember) => m.organization_id === org.id),
        }))
      );
    } catch {
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrgs();
  }, []);

  const createOrg = async (name: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('organizations').insert({ name: name.trim() });
      if (!error) {
        await loadOrgs();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const removeMember = async (userId: string, orgId: string): Promise<'ok' | 'error'> => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return 'error';
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ user_id: userId, organization_id: orgId }),
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      await loadOrgs();
      return 'ok';
    } catch {
      return 'error';
    }
  };

  const updateOrgSettings = async (orgId: string, values: Partial<OrgSettingsInput>): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('organizations')
        .update(values)
        .eq('id', orgId);
      if (error) throw error;
      await loadOrgs();
      return true;
    } catch {
      return false;
    }
  };

  return { orgs, loading, createOrg, removeMember, updateOrgSettings };
}
